import { Link } from 'react-router-dom'
import { IconAlertTriangle } from '@tabler/icons-react'
import type { InboxCounts, PastaDaEntrada } from '@/api/chamados'
import { WidgetCard } from '@/components/ui/WidgetCard'
import { CardHeader } from '@/components/ui/CardHeader'
import { PASTAS, pastaVisivel } from './pastas'
import classes from './Entrada.module.css'

interface PastasCardProps {
  ativa: PastaDaEntrada
  counts: InboxCounts | undefined
  /** A contagem falhou: as pastas ficam, sem número, e a tela diz que faltou o número. */
  contagemFalhou: boolean
}

function nomeAcessivel(rotulo: string, total: number, negrito: number): string {
  if (!total) return rotulo
  const chamados = total === 1 ? '1 chamado' : `${total} chamados`
  return negrito ? `${rotulo}, ${chamados}, ${negrito} com movimento novo` : `${rotulo}, ${chamados}`
}

/**
 * Cartão Pastas (spec §3, §4). Não usa o `ChamadoFolderNav` de maio: as pastas dele são fixas
 * (sent/resolved/archived, que esta Entrada não tem) e o contador some no zero sem distinguir
 * pasta vazia de contagem que falhou.
 */
export function PastasCard({ ativa, counts, contagemFalhou }: PastasCardProps) {
  const negrito = counts?.negrito_por_pasta ?? {}
  return (
    <WidgetCard p={0}>
      <CardHeader title="Pastas" />
      <nav aria-label="Pastas">
        <ul className={classes.pastas}>
          {PASTAS.filter((p) => pastaVisivel(p.chave, counts, ativa)).map(({ chave, rotulo, Icon }) => {
            const total = counts?.[chave] ?? 0
            const emNegrito = negrito[chave] ?? 0
            return (
              <li key={chave}>
                <Link
                  to={`?pasta=${chave}`}
                  className={classes.pasta}
                  aria-current={chave === ativa ? 'page' : undefined}
                  aria-label={nomeAcessivel(rotulo, total, emNegrito)}
                  data-vazia={total === 0}
                  data-negrito={emNegrito > 0}
                >
                  <Icon size={16} aria-hidden />
                  <span className={classes.pastaRotulo}>{rotulo}</span>
                  {total > 0 && <span className={classes.contador}>{total}</span>}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
      {contagemFalhou && (
        <div className={classes.avisoContagem} role="status">
          <IconAlertTriangle size={14} aria-hidden />
          Não foi possível carregar o número de chamados de cada pasta.
        </div>
      )}
    </WidgetCard>
  )
}
